import { getSchemaForDatabase } from '../utils/IndexedDBSchemaRegistry';

interface IVersionedIndexedDBRecord {
    id: string;
    versionId?: string;
}

interface IIndexedDBIndexInfo {
    indexName: string;
    query: IDBValidKey | IDBKeyRange | null;
}

export interface IUseIndexedDBHook<T extends IVersionedIndexedDBRecord> {
    insertRecord: (databaseName: string, storeName: string, record: T) => Promise<unknown>;
    getRecord: (databaseName: string, storeName: string, id: string) => Promise<T | undefined>;
    deleteRecord: (databaseName: string, storeName: string, id: string) => Promise<void>;
    removePreviousVersions: (
        databaseName: string,
        storeName: string,
        versionId: string,
        indexInfo: IIndexedDBIndexInfo
    ) => Promise<unknown>;
    deleteDatabase: (databaseName: string) => Promise<unknown>;
    initializeStore: (databaseName: string, storeName: string) => Promise<IDBDatabase>;
}

export function useIndexedDB<T extends IVersionedIndexedDBRecord>(dbFactory: IDBFactory, version: number): IUseIndexedDBHook<T> {
    const ensureStoreInitialized = async (databaseName: string, storeName: string): Promise<IDBDatabase> => {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = dbFactory.open(databaseName, version);

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result;
                const storeNames = Array.from(db.objectStoreNames);

                // Clean up old stores (keep only the current one)
                for (const name of storeNames) {
                    if (name !== storeName) {
                        db.deleteObjectStore(name);
                    }
                }

                // Create store if it doesn't exist
                if (!db.objectStoreNames.contains(storeName)) {
                    const schemaFunction = getSchemaForDatabase(databaseName);
                    schemaFunction(db, storeName);
                }
            };

            request.onsuccess = (event: Event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                resolve(db);
            };

            request.onerror = () => reject(request.error);
        });
    };
    // Helper to open and close DB connection
    const withDB = async <R>(databaseName: string, storeName: string, fn: (db: IDBDatabase) => Promise<R>): Promise<R> => {
        const db = await ensureStoreInitialized(databaseName, storeName);
        try {
            const result = await fn(db);
            return result;
        } finally {
            db.close();
        }
    };

    const putRecord = async (db: IDBDatabase, storeName: string, data: any) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        return new Promise((resolve) => {
            const result = store.put(data);
            result.onsuccess = () => {
                resolve(result.result);
            };
            result.onerror = () => {
                resolve(result.error);
            };
        });
    };

    const insertRecord = async (databaseName: string, storeName: string, record: T) => {
        return withDB(databaseName, storeName, (db) => putRecord(db, storeName, record as any));
    };

    const getRecordFromDB = async (db: IDBDatabase, storeName: string, id: string): Promise<any | null> => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        return new Promise((resolve, reject) => {
            const result = store.get(id);
            result.onsuccess = () => {
                resolve(result.result);
            };
            result.onerror = () => {
                reject(result.error);
            };
        });
    };

    const getRecord = async (databaseName: string, storeName: string, id: string): Promise<T | undefined> => {
        return withDB(databaseName, storeName, (db) => getRecordFromDB(db, storeName, id)).then((record) => {
            return record as T | undefined;
        });
    };

    const deleteRecord = async (databaseName: string, storeName: string, id: string): Promise<void> => {
        return withDB(databaseName, storeName, (db) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            return new Promise((resolve, reject) => {
                const deleteRequest = store.delete(id);
                deleteRequest.onsuccess = () => resolve();
                deleteRequest.onerror = () => reject(deleteRequest.error);
            });
        });
    };

    const removePreviousVersions = async (databaseName: string, storeName: string, versionId: string, indexInfo: IIndexedDBIndexInfo) => {
        return withDB(databaseName, storeName, (db) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const { indexName, query } = indexInfo;
            return new Promise((resolve) => {
                const records = store.index(indexName).getAll(query);
                records.onsuccess = () => {
                    records.result.forEach((record: T) => {
                        if (record.versionId !== versionId) {
                            store.delete(record.id);
                        }
                    });

                    resolve(null);
                };

                records.onerror = () => {
                    resolve(records.error);
                };
            });
        });
    };

    const deleteDatabase = async (databaseName: string) => {
        const deleteRequest = dbFactory.deleteDatabase(databaseName);
        return new Promise((resolve, reject) => {
            deleteRequest.onsuccess = () => {
                resolve(deleteRequest.result);
            };
            deleteRequest.onerror = () => {
                reject(deleteRequest.error);
            };
        });
    };

    return {
        insertRecord,
        getRecord,
        deleteRecord,
        removePreviousVersions,
        deleteDatabase,
        initializeStore: ensureStoreInitialized,
    };
}
